/**
 * Answer model based on API response structure
 */

/**
 * @typedef {Object} Language
 * @property {string} code - Language code (e.g., "fr", "en", "es")
 */

/**
 * @typedef {Object} AnswerTranslation
 * @property {string} answerText - The answer text in the specific language
 * @property {Language} language - The language object
 */

/**
 * @typedef {Object} User
 * @property {string} id - User ID
 */

/**
 * @typedef {Object} Answer
 * @property {string} id - Answer ID
 * @property {string} createdAt - Creation date in ISO format
 * @property {string} updatedAt - Last update date in ISO format
 * @property {User} user - User object containing the user ID
 * @property {string} questionId - ID of the associated question
 * @property {string} userRole - Role of the user who provided the answer
 * @property {AnswerTranslation[]} answerTranslations - Array of answer translations
 * @property {string|null} [deletedAt] - Soft delete timestamp from API
 */

/**
 * Get the answer text in the specified language
 * @param {Answer|Object} answer - The answer object (can be full API format or transformed format)
 * @param {string} languageCode - The language code to get the text for (default: "fr")
 * @returns {string} The answer text in the specified language
 */
export function getAnswerText(answer, languageCode = "fr") {
  if (!answer) {
    return "No answer text available";
  }
  
  // Handle transformed format (has direct 'answer' field)
  if (answer.answer && typeof answer.answer === 'string') {
    return answer.answer;
  }
  
  // Handle full API format (has 'answerTranslations' array)
  if (answer.answerTranslations && Array.isArray(answer.answerTranslations)) {
    // Try to find the exact language match
    const translation = answer.answerTranslations.find(t => t.language?.code === languageCode);
    if (!translation) return null
    else if (translation?.answerText) return translation.answerText;
  }
  
  return null
}

/**
 * Transform API answer data to internal format
 * @param {Answer} apiAnswer - Answer data from API
 * @param {string} commonLanguage - Common language code for the user
 * @returns {Object} Transformed answer object for internal use
 */
export function transformAnswer(apiAnswer, commonLanguage = "fr") {
  if (!apiAnswer || typeof apiAnswer !== 'object') {
    throw new Error('Invalid answer data provided to transformAnswer');
  }
  
  if (getAnswerText(apiAnswer, commonLanguage) === null) {
    return null;
  }

  return {
    id: apiAnswer.id || '',
    answer: getAnswerText(apiAnswer, commonLanguage),
    questionId: apiAnswer.questionId || null,
    userRole: apiAnswer.userRole || 'Public',
    userId: apiAnswer.user?.id || null,
    createdAt: apiAnswer.createdAt || new Date().toISOString(),
    updatedAt: apiAnswer.updatedAt || new Date().toISOString(),
    deletedAt: apiAnswer.deletedAt || null,
    answerTranslations: Array.isArray(apiAnswer.answerTranslations) ? apiAnswer.answerTranslations : []
  };
}

/**
 * Transform internal answer data to API format
 * @param {Object} answer - Internal answer data
 * @returns {Object} API-formatted answer
 */
export function transformAnswerToApi(answer) {
  if (!answer) {
    return null;
  }

  return {
    id: answer.id,
    userId: answer.userId,
    questionId: answer.questionId,
    userRole: answer.userRole,
    answerTranslations: answer.answerTranslations?.map(t => ({
      language: { code: t.language },
      answerText: t.answerText
    })) || []
  };
}
